import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { toast } from '../../utils/toast'
import { useDispatch } from 'react-redux'
import {
  CalendarMinusIcon,
  CalendarDotsIcon,
  PlusIcon,
  ListIcon,
  ArrowsDownUpIcon,
  PencilSimpleIcon,
  TrashSimpleIcon,
} from '@phosphor-icons/react'
import { TimeOff } from '../../types/TimeOff'
import { User } from '../../types/User'
import { useTimeoffCalculator } from '../../hooks/useTimeoffCalculator'
import { GoogleCalendarConsentModal } from '../../components/TimeOffForm/TimeOffForm'
import { GoogleCalendarSettingsModal } from '../../components/GoogleCalendarSettingsModal/GoogleCalendarSettingsModal'
import LogTimeOffModal, {
  TimeOffFormData,
} from '../../components/LogTimeOffModal/LogTimeOffModal'
import DeleteTimeOffModal from '../../components/LogTimeOffModal/DeleteTimeOffModal'
import { authContext } from '../../components/CognitoContextProvider/CognitoContextProvider'
import { timeoffApi } from '../../redux/timeoff.api'
import {
  useArchiveTimeOffMutation,
  useCreateTimeOffMutation,
  useLazyGetAllTimeoffsQuery,
  useLazyGetGoogleCalendarOAuthStartQuery,
  useUpdateTimeOffMutation,
} from '../../redux/timeoff.endpoints'
import { submitTimeOffUpdate } from '../../utils/timeoffUpdate'
import googleCalendarLogo from '../../assets/google_calendar_logo.png'

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

interface Props {
  profile: User
}

const PersonalTimeOff = ({ profile }: Props) => {
  const dispatch = useDispatch()
  const { auth } = useContext(authContext)

  const { getTotalDaysUsed, getDaysPlanned, getBusinessDaysForTimeoff } =
    useTimeoffCalculator()
  const [view, setView] = useState<'calendar' | 'list'>('calendar')
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc')
  const [timeOffs, setTimeOffs] = useState<TimeOff[]>([])
  const [isEditTimeoffModalOpen, setEditTimeoffModalOpen] = useState(false)
  const [isArchiveTimeOffModalOpen, setIsArchiveTimeOffModalOpen] =
    useState(false)
  const [selectedTimeoff, setSelectedTimeoff] = useState({} as TimeOff)
  const [showLogModal, setShowLogModal] = useState(false)
  const [isGoogleModalOpen, setIsGoogleModalOpen] = useState(false)
  const [showCalSettings, setShowCalSettings] = useState(false)
  const [isConnectingGoogle, setIsConnectingGoogle] = useState(false)
  const [pendingLogData, setPendingLogData] = useState<{
    startDate: string
    endDate: string
    description: string
  } | null>(null)

  const [updateTimeOff, updateTimeOffResult] = useUpdateTimeOffMutation()
  const [getAllTimesOff, getAllTimesOffResponse] = useLazyGetAllTimeoffsQuery()
  const [archiveTimeOff, archiveTimeOffResult] = useArchiveTimeOffMutation()
  const [createTimeOff] = useCreateTimeOffMutation()
  const [getGoogleCalendarOAuthStart] =
    useLazyGetGoogleCalendarOAuthStartQuery()

  const idToken = useMemo(() => {
    if (!auth.data?.signInUserSession?.idToken?.jwtToken) return ''
    return auth.data.signInUserSession.idToken.jwtToken
  }, [auth.data])

  const daysUsed = getTotalDaysUsed(timeOffs)
  const daysPlanned = getDaysPlanned(timeOffs)
  const currentYear = new Date().getFullYear()

  const sortedTimeOffs = useMemo(
    () =>
      [...timeOffs].sort((a, b) => {
        const dateA = new Date(a.startDate).getTime()
        const dateB = new Date(b.startDate).getTime()
        return sortDir === 'asc' ? dateA - dateB : dateB - dateA
      }),
    [timeOffs, sortDir],
  )

  const openEditTimeOffModal = (timeoff: TimeOff) => {
    setSelectedTimeoff(timeoff)
    setEditTimeoffModalOpen(true)
  }

  const openArchiveTimeOffModal = (timeoff: TimeOff) => {
    setSelectedTimeoff(timeoff)
    setIsArchiveTimeOffModalOpen(true)
  }

  const handleAddTimeOff = () => setShowLogModal(true)

  const waitForGoogleOAuthResult = (popup: Window | null) =>
    new Promise<{ success: boolean; message?: string }>(resolve => {
      const onMessage = (event: MessageEvent) => {
        const payload = event.data
        if (!payload || payload.source !== 'google-calendar-oauth') return
        cleanup()
        resolve({ success: Boolean(payload.success), message: payload.message })
      }
      const cleanup = () => {
        clearInterval(interval)
        clearTimeout(timeout)
        window.removeEventListener('message', onMessage)
      }
      const interval = window.setInterval(() => {
        if (popup && popup.closed) {
          cleanup()
          resolve({
            success: false,
            message: 'Google login window was closed before completing auth.',
          })
        }
      }, 500)
      const timeout = window.setTimeout(() => {
        cleanup()
        resolve({
          success: false,
          message: 'Google login timed out. Please try again.',
        })
      }, 120000)
      window.addEventListener('message', onMessage)
    })

  const submitLogTimeOff = async (
    data: { startDate: string; endDate: string; description: string },
    skipCalendarCheck = false,
  ): Promise<boolean> => {
    const syncEnabled = profile.googleCalendarSyncEnabled ?? true
    if (
      !skipCalendarCheck &&
      syncEnabled &&
      !profile.hasGoogleCalendarConnected
    ) {
      setPendingLogData(data)
      setIsGoogleModalOpen(true)
      return false
    }

    try {
      await createTimeOff({
        userIdWhoCreatedTimeoff: profile.id,
        timeoffs: [
          {
            name: '',
            description: data.description,
            approved: false,
            userId: profile.id,
            startDate: data.startDate,
            endDate: data.endDate,
          },
        ],
      }).unwrap()
      setShowLogModal(false)
      setPendingLogData(null)
      getAllTimesOff(profile.id.toString())
      toast.success('Time Off created successfully', {
        toastId: 'create-timeoff-success',
      })
      return true
    } catch {
      toast.error('Something went wrong', { toastId: 'create-timeoff-error' })
      return false
    }
  }

  const submitWithGoogleLogin = async () => {
    if (!idToken || !pendingLogData) {
      toast.error('Unable to start Google Calendar integration.', {
        toastId: 'google-oauth-start-error',
      })
      return
    }
    setIsConnectingGoogle(true)
    try {
      const oauthStart = await getGoogleCalendarOAuthStart(idToken).unwrap()
      const popup = window.open(
        oauthStart.authorizationUrl,
        'google-calendar-oauth',
        'width=600,height=700',
      )
      if (!popup) {
        toast.error(
          'Unable to open Google login window. Check popup permissions.',
          { toastId: 'google-popup-error' },
        )
        return
      }
      const result = await waitForGoogleOAuthResult(popup)
      if (result.success) {
        dispatch(timeoffApi.util.invalidateTags(['LoggedInUser']))
        toast.success('Google Calendar connected successfully.', {
          toastId: 'google-connected-success',
        })
        setIsGoogleModalOpen(false)
        await submitLogTimeOff(pendingLogData, true)
        return
      }
      toast.warning(
        result.message || 'Google login did not finish. Please try again.',
        { toastId: 'google-connected-warning' },
      )
    } catch {
      toast.error('Unable to start Google Calendar integration.', {
        toastId: 'google-oauth-start-error',
      })
    } finally {
      setIsConnectingGoogle(false)
    }
  }

  const submitUpdateTimeOff = async (
    data: TimeOffFormData,
  ): Promise<boolean> => {
    const updatedTimeoff = {
      ...selectedTimeoff,
      startDate: moment(data.startDate).toDate(),
      endDate: moment(data.endDate).toDate(),
      description: data.description,
    }
    setSelectedTimeoff(updatedTimeoff)
    return submitTimeOffUpdate({
      selectedTimeoff: updatedTimeoff,
      allTimesOff: timeOffs,
      timeOffOwner: profile,
      profile,
      updateTimeOff: args => updateTimeOff(args),
    })
  }

  useEffect(() => {
    getAllTimesOff(profile.id.toString())
  }, [profile.id])

  useEffect(() => {
    if (getAllTimesOffResponse?.data) {
      setTimeOffs(getAllTimesOffResponse.data)
    }
  }, [getAllTimesOffResponse])

  useEffect(() => {
    if (updateTimeOffResult?.isSuccess) {
      setEditTimeoffModalOpen(false)
      toast.success('Time Off updated successfully', {
        toastId: 'update-timeoff-success',
      })
      getAllTimesOff(profile.id.toString())
    }
  }, [updateTimeOffResult])

  useEffect(() => {
    if (!updateTimeOffResult.isError) return

    const errorData = updateTimeOffResult.error as {
      data?: { error?: string }
    }

    toast.error(
      errorData?.data?.error || 'Unable to update Time Off. Please try again.',
      {
        toastId: 'update-timeoff-error',
      },
    )
  }, [updateTimeOffResult.isError])

  useEffect(() => {
    if (archiveTimeOffResult?.isSuccess) {
      toast.success('Time Off deleted successfully!', {
        toastId: 'archive-timeoff-success',
      })
      setTimeOffs(timeOffs.filter(timeOff => timeOff.id != selectedTimeoff.id))
    }
  }, [archiveTimeOffResult])

  return (
    <>
      <LogTimeOffModal
        key={selectedTimeoff.id ?? 'edit-time-off'}
        show={isEditTimeoffModalOpen}
        onClose={() => setEditTimeoffModalOpen(false)}
        onSubmit={submitUpdateTimeOff}
        mode="edit"
        initialValues={
          selectedTimeoff.id
            ? {
                startDate: selectedTimeoff.startDate,
                endDate: selectedTimeoff.endDate,
                description: selectedTimeoff.description ?? '',
              }
            : undefined
        }
        onDelete={() => {
          setEditTimeoffModalOpen(false)
          setIsArchiveTimeOffModalOpen(true)
        }}
      />
      <DeleteTimeOffModal
        show={isArchiveTimeOffModalOpen}
        onClose={() => setIsArchiveTimeOffModalOpen(false)}
        onConfirm={() => {
          archiveTimeOff({ selectedTimeoff, archivedBy: profile })
          setIsArchiveTimeOffModalOpen(false)
        }}
      />
      <div className="personal-time-off">
      <h1 className="personal-time-off__title">My Time Off</h1>

      <div className="personal-time-off__stats">
        <div className="personal-time-off__stat-card">
          <div className="personal-time-off__stat-value-row">
            <CalendarMinusIcon size={20} weight="regular" color="#1F0E38" />
            <span className="personal-time-off__stat-value">{daysUsed}</span>
          </div>
          <div className="personal-time-off__stat-label">
            days used this year
          </div>
        </div>
        <div className="personal-time-off__stat-card">
          <div className="personal-time-off__stat-value-row">
            <CalendarDotsIcon size={20} weight="regular" color="#2A9D8F" />
            <span className="personal-time-off__stat-value personal-time-off__stat-value--planned">
              {daysPlanned}
            </span>
          </div>
          <div className="personal-time-off__stat-label">days planned</div>
        </div>
      </div>

      <div className="personal-time-off__actions">
        <div className="personal-time-off__view-toggle">
          <button
            type="button"
            className={`personal-time-off__view-toggle-btn${
              view === 'calendar'
                ? ' personal-time-off__view-toggle-btn--active'
                : ''
            }`}
            onClick={() => setView('calendar')}
          >
            <CalendarDotsIcon
              size={14}
              weight="regular"
              color={view === 'calendar' ? '#0A0A0A' : '#717182'}
            />
            Calendar
          </button>
          <button
            type="button"
            className={`personal-time-off__view-toggle-btn${
              view === 'list'
                ? ' personal-time-off__view-toggle-btn--active'
                : ''
            }`}
            onClick={() => setView('list')}
          >
            <ListIcon
              size={14}
              weight="regular"
              color={view === 'list' ? '#0A0A0A' : '#717182'}
            />
            List
          </button>
        </div>
        <div className="personal-time-off__actions-right">
          <button
            type="button"
            className="personal-time-off__gcal-settings-btn"
            onClick={() => setShowCalSettings(true)}
          >
            <img
              src={googleCalendarLogo}
              alt="Google Calendar"
              className="personal-time-off__gcal-settings-icon"
            />
            Google Calendar Settings
          </button>
          <button
            type="button"
            className="personal-time-off__log-btn"
            onClick={handleAddTimeOff}
          >
            <PlusIcon size={16} weight="regular" color="#ffffff" />
            Log Time Off
          </button>
        </div>
      </div>

      {view === 'calendar' ? (
        <YearCalendarView timeOffs={timeOffs} year={currentYear} />
      ) : (
        <ListView
          timeOffs={sortedTimeOffs}
          profile={profile}
          getBusinessDaysForTimeoff={getBusinessDaysForTimeoff}
          onToggleSort={() =>
            setSortDir(direction => (direction === 'asc' ? 'desc' : 'asc'))
          }
          onEditTimeOff={openEditTimeOffModal}
          onDeleteTimeOff={openArchiveTimeOffModal}
        />
      )}
      </div>

      <LogTimeOffModal
        show={showLogModal}
        onClose={() => setShowLogModal(false)}
        onSubmit={submitLogTimeOff}
      />

      <GoogleCalendarConsentModal
        show={isGoogleModalOpen}
        isConnecting={isConnectingGoogle}
        onClose={() => setIsGoogleModalOpen(false)}
        onConnect={submitWithGoogleLogin}
      />

      <GoogleCalendarSettingsModal
        show={showCalSettings}
        onClose={() => setShowCalSettings(false)}
        idToken={idToken}
      />
    </>
  )
}

function YearCalendarView({
  timeOffs,
  year,
}: {
  timeOffs: TimeOff[]
  year: number
}) {
  const offSet = useMemo(() => {
    const set = new Set<string>()
    timeOffs.forEach(timeoff => {
      const start = moment(timeoff.startDate).startOf('day')
      const end = moment(timeoff.endDate).startOf('day')
      const current = start.clone()
      while (current.isSameOrBefore(end, 'day')) {
        if (current.year() === year) {
          const dayOfWeek = current.day()
          if (dayOfWeek !== 0 && dayOfWeek !== 6) {
            set.add(`${current.year()}-${current.month()}-${current.date()}`)
          }
        }
        current.add(1, 'day')
      }
    })
    return set
  }, [timeOffs, year])

  return (
    <div className="personal-time-off__calendar-card">
      <div className="personal-time-off__calendar-header">
        <h2 className="personal-time-off__calendar-heading">Yearly Overview</h2>
        <div className="personal-time-off__calendar-year">{year}</div>
      </div>
      <div className="personal-time-off__months-grid">
        {MONTHS.map((month, index) => (
          <MiniMonth
            key={month}
            year={year}
            monthIdx={index}
            title={month}
            offSet={offSet}
          />
        ))}
      </div>
    </div>
  )
}

function MiniMonth({
  year,
  monthIdx,
  title,
  offSet,
}: {
  year: number
  monthIdx: number
  title: string
  offSet: Set<string>
}) {
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate()
  const firstWeekday = new Date(year, monthIdx, 1).getDay()
  const cells: (number | null)[] = []

  for (let i = 0; i < firstWeekday; i++) cells.push(null)
  for (let day = 1; day <= daysInMonth; day++) cells.push(day)
  while (cells.length % 7 !== 0) cells.push(null)

  const today = new Date()
  const isToday = (day: number) =>
    today.getFullYear() === year &&
    today.getMonth() === monthIdx &&
    today.getDate() === day
  const isOff = (day: number) => offSet.has(`${year}-${monthIdx}-${day}`)

  return (
    <div>
      <div className="personal-time-off__mini-month-title">{title}</div>
      <div className="personal-time-off__mini-month-grid">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, index) => (
          <div
            key={`${title}-weekday-${index}`}
            className="personal-time-off__weekday-label"
          >
            {label}
          </div>
        ))}
        {cells.map((day, index) => {
          if (day === null) {
            return (
              <div
                key={`${title}-empty-${index}`}
                className="personal-time-off__day-cell personal-time-off__day-cell--empty"
              />
            )
          }

          const weekday = (firstWeekday + day - 1) % 7
          const weekend = weekday === 0 || weekday === 6
          const off = isOff(day)
          const todayCell = isToday(day)

          return (
            <div
              key={`${title}-day-${day}`}
              className={[
                'personal-time-off__day-cell',
                weekend ? 'personal-time-off__day-cell--weekend' : '',
                off ? 'personal-time-off__day-cell--off' : '',
                todayCell ? 'personal-time-off__day-cell--today' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {day}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function ListView({
  timeOffs,
  profile,
  getBusinessDaysForTimeoff,
  onToggleSort,
  onEditTimeOff,
  onDeleteTimeOff,
}: {
  timeOffs: TimeOff[]
  profile: User
  getBusinessDaysForTimeoff: (timeoff: TimeOff, isListView?: boolean) => number
  onToggleSort: () => void
  onEditTimeOff: (timeoff: TimeOff) => void
  onDeleteTimeOff: (timeoff: TimeOff) => void
}) {
  if (timeOffs.length === 0) {
    return (
      <div className="personal-time-off__list-card">
        <p className="personal-time-off__empty">No time off entries yet.</p>
      </div>
    )
  }

  return (
    <div className="personal-time-off__list-card">
      <table className="personal-time-off__table">
        <thead className="personal-time-off__table-head">
          <tr>
            <th className="personal-time-off__table-th">
              <button
                type="button"
                className="personal-time-off__sort-btn"
                onClick={onToggleSort}
              >
                Start Date
                <ArrowsDownUpIcon size={14} weight="regular" color="#717182" />
              </button>
            </th>
            <th className="personal-time-off__table-th">End Date</th>
            <th className="personal-time-off__table-th personal-time-off__table-th--right">
              Days
            </th>
            <th className="personal-time-off__table-th">Notes</th>
            <th className="personal-time-off__table-th personal-time-off__table-th--actions" />
          </tr>
        </thead>
        <tbody>
          {timeOffs.map(timeoff => {
            const notes = timeoff.description ?? ''
            const truncated =
              notes.length > 40 ? `${notes.slice(0, 40)}...` : notes
            const isOwner = profile.id === timeoff.userId
            const hasPassed = moment(timeoff.endDate)
              .startOf('day')
              .isBefore(moment().startOf('day'))
            const canManage =
              isOwner && (profile.isAdmin || !hasPassed)

            return (
              <tr key={timeoff.id} className="personal-time-off__table-row">
                <td className="personal-time-off__table-td">
                  {moment(timeoff.startDate).format('MMM D, YYYY')}
                </td>
                <td className="personal-time-off__table-td">
                  {moment(timeoff.endDate).format('MMM D, YYYY')}
                </td>
                <td className="personal-time-off__table-td personal-time-off__table-td--right">
                  {getBusinessDaysForTimeoff(timeoff, true)}
                </td>
                <td
                  className="personal-time-off__table-td personal-time-off__table-td--notes"
                  title={notes || undefined}
                >
                  {truncated || '—'}
                </td>
                <td className="personal-time-off__table-td personal-time-off__table-td--actions">
                  {canManage && (
                    <div className="personal-time-off__row-actions">
                      <button
                        type="button"
                        className="personal-time-off__icon-btn"
                        aria-label="Edit time off"
                        onClick={() => onEditTimeOff(timeoff)}
                      >
                        <PencilSimpleIcon size={18} weight="regular" />
                      </button>
                      <button
                        type="button"
                        className="personal-time-off__icon-btn personal-time-off__icon-btn--delete"
                        aria-label="Delete time off"
                        onClick={() => onDeleteTimeOff(timeoff)}
                      >
                        <TrashSimpleIcon size={18} weight="regular" />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default PersonalTimeOff
